import React, { createContext, useContext, useState, useMemo } from "react";
import { colors } from "./tokens/colors";
import { spacing, radiusScale } from "./tokens/spacing";
import { shape } from "./tokens/shape";
import { shadow } from "./tokens/shadows";
import { getFonts } from "./tokens/fonts";
import {
  ProductContext,
  getTypographyTokens,
  defaultProductContext,
} from "./tokens/typography";
import { FontLoader } from "./fonts/FontLoader";
import { TypographyStyleLoader } from "./styles/TypographyStyleLoader";

export type ThemeMode =
  | "dark"
  | "light"
  | "pentagram-dark"
  | "pentagram-light"
  | "ltg-dark"
  | "blueprints";

const DEPRECATED_MODES = new Set([
  "xsolla-dark",
  "xsolla-light",
  "xsollaDark",
  "xsollaLight",
]);

export const themeConfig = (
  mode: ThemeMode = "dark",
  productContext: ProductContext = defaultProductContext
) => {
  if (DEPRECATED_MODES.has(mode)) {
    console.warn(
      `Theme mode "${mode}" is deprecated and will be removed in the next major update. Please migrate to "dark" or "light".`
    );
  }
  const contextFonts = getFonts(productContext);
  return {
    colors: colors[mode as keyof typeof colors],
    spacing,
    shape,
    shadow,
    fonts: contextFonts,
    typography: {
      primary: contextFonts.body,
      heading: contextFonts.heading,
      body: contextFonts.body,
    },
    typographyTokens: getTypographyTokens(productContext),
    productContext,
    sizing: {
      button: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            height: 64,
            padding: 20,
            fontSize: 20,
            lineHeight: 22,
            sublabelFontSize: 18,
            spinnerSize: 24,
            iconSize: 32,
            iconContainerSize: 64,
            borderRadius: shape.button.xl.borderRadius,
            labelIconSize: 22,
            labelIconGap: 6,
          },
          lg: {
            height: 56,
            padding: 18,
            fontSize: 18,
            lineHeight: 20,
            sublabelFontSize: 16,
            spinnerSize: 24,
            iconSize: 28,
            iconContainerSize: 56,
            borderRadius: shape.button.lg.borderRadius,
            labelIconSize: 20,
            labelIconGap: 4,
          },
          md: {
            height: 48,
            padding: 16,
            fontSize: 16,
            lineHeight: 18,
            sublabelFontSize: 14,
            spinnerSize: 24,
            iconSize: 24,
            iconContainerSize: 48,
            borderRadius: shape.button.md.borderRadius,
            labelIconSize: 18,
            labelIconGap: 4,
          },
          sm: {
            height: 40,
            padding: 14,
            fontSize: 14,
            lineHeight: 16,
            sublabelFontSize: 12,
            spinnerSize: 24,
            iconSize: 20,
            iconContainerSize: 40,
            borderRadius: shape.button.sm.borderRadius,
            labelIconSize: 16,
            labelIconGap: 4,
          },
          xs: {
            height: 32,
            padding: 12,
            fontSize: 12,
            lineHeight: 14,
            sublabelFontSize: 10,
            spinnerSize: 16,
            iconSize: 16,
            iconContainerSize: 32,
            borderRadius: shape.button.xs.borderRadius,
            labelIconSize: 14,
            labelIconGap: 4,
          },
        };
        return configs[size];
      },
      flexButton: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            height: 36,
            padding: 4,
            fontSize: 16,
            spinnerSize: 28,
            iconSize: 20,
            borderRadius: radiusScale["50"],
          },
          lg: {
            height: 32,
            padding: 4,
            fontSize: 14,
            spinnerSize: 24,
            iconSize: 18,
            borderRadius: radiusScale["50"],
          },
          md: {
            height: 28,
            padding: 2,
            fontSize: 14,
            spinnerSize: 24,
            iconSize: 16,
            borderRadius: radiusScale["25"],
          },
          sm: {
            height: 22,
            padding: 2,
            fontSize: 12,
            spinnerSize: 16,
            iconSize: 14,
            borderRadius: radiusScale["25"],
          },
          xs: {
            height: 20,
            padding: 2,
            fontSize: 12,
            spinnerSize: 16,
            iconSize: 12,
            borderRadius: radiusScale["25"],
          },
        };
        return configs[size];
      },
      checkbox: (size: "xl" | "lg" | "md" | "sm") => {
        const configs = {
          xl: {
            size: 22,
            fontSize: 18,
            descriptionFontSize: 16,
            labelGap: 12,
            textGap: 4,
            borderRadius: shape.checkbox.xl.borderRadius,
          },
          lg: {
            size: 20,
            fontSize: 16,
            descriptionFontSize: 14,
            labelGap: 10,
            textGap: 2,
            borderRadius: shape.checkbox.lg.borderRadius,
          },
          md: {
            size: 18,
            fontSize: 16,
            descriptionFontSize: 14,
            labelGap: 8,
            textGap: 2,
            borderRadius: shape.checkbox.md.borderRadius,
          },
          sm: {
            size: 16,
            fontSize: 14,
            descriptionFontSize: 12,
            labelGap: 8,
            textGap: 0,
            borderRadius: shape.checkbox.sm.borderRadius,
          },
        };
        return configs[size];
      },
      radio: (size: "xl" | "lg" | "md" | "sm") => {
        const configs = {
          xl: {
            size: 24,
            iconSize: 10,
            fontSize: 20,
            lineHeight: 22,
            descriptionFontSize: 18,
            descriptionLineHeight: 20,
            labelGap: 12,
            textGap: 4,
            borderWidth: 1,
          },
          lg: {
            size: 20,
            iconSize: 8,
            fontSize: 18,
            lineHeight: 20,
            descriptionFontSize: 16,
            descriptionLineHeight: 18,
            labelGap: 10,
            textGap: 2,
            borderWidth: 1,
          },
          md: {
            size: 18,
            iconSize: 8,
            fontSize: 16,
            lineHeight: 18,
            descriptionFontSize: 14,
            descriptionLineHeight: 16,
            labelGap: 8,
            textGap: 2,
            borderWidth: 1,
          },
          sm: {
            size: 16,
            iconSize: 6,
            fontSize: 14,
            lineHeight: 16,
            descriptionFontSize: 12,
            descriptionLineHeight: 14,
            labelGap: 6,
            textGap: 0,
            borderWidth: 1,
          },
        };
        return configs[size];
      },
      input: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            height: 64,
            paddingVertical: 12,
            paddingHorizontal: 12,
            fontSize: 20,
            iconSize: 18,
            radius: shape.input.xl.borderRadius,
            borderWidth: 2,
            fieldGap: 8,
            lineHeight: 20,
          },
          lg: {
            height: 56,
            paddingVertical: 14,
            paddingHorizontal: 12,
            fontSize: 18,
            iconSize: 18,
            radius: shape.input.lg.borderRadius,
            borderWidth: 2,
            fieldGap: 6,
            lineHeight: 18,
          },
          md: {
            height: 48,
            paddingVertical: 11,
            paddingHorizontal: 12,
            fontSize: 16,
            iconSize: 18,
            radius: shape.input.md.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
            lineHeight: 16,
          },
          sm: {
            height: 40,
            paddingVertical: 7,
            paddingHorizontal: 10,
            fontSize: 14,
            iconSize: 18,
            radius: shape.input.sm.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
            lineHeight: 14,
          },
          xs: {
            height: 32,
            paddingVertical: 7,
            paddingHorizontal: 10,
            fontSize: 12,
            iconSize: 18,
            radius: shape.input.xs.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
            lineHeight: 10,
          },
        };
        return configs[size];
      },
      imageUploader: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            box: 200,
            padding: 14,
            gap: 10,
            iconSize: 32,
            spinnerSize: "lg" as const,
            labelFontSize: 20,
            labelLineHeight: 22,
            descriptionFontSize: 20,
            descriptionLineHeight: 22,
            errorFontSize: 16,
            errorLineHeight: 18,
            errorGap: 8,
            radius: shape.input.xl.borderRadius,
            borderWidth: 1,
          },
          lg: {
            box: 168,
            padding: 14,
            gap: 10,
            iconSize: 24,
            spinnerSize: "lg" as const,
            labelFontSize: 18,
            labelLineHeight: 24,
            descriptionFontSize: 18,
            descriptionLineHeight: 20,
            errorFontSize: 16,
            errorLineHeight: 18,
            errorGap: 8,
            radius: shape.input.lg.borderRadius,
            borderWidth: 1,
          },
          md: {
            box: 136,
            padding: 12,
            gap: 8,
            iconSize: 24,
            spinnerSize: "md" as const,
            labelFontSize: 16,
            labelLineHeight: 20,
            descriptionFontSize: 16,
            descriptionLineHeight: 20,
            errorFontSize: 14,
            errorLineHeight: 16,
            errorGap: 6,
            radius: shape.input.md.borderRadius,
            borderWidth: 1,
          },
          sm: {
            box: 104,
            padding: 10,
            gap: 6,
            iconSize: 18,
            spinnerSize: "sm" as const,
            labelFontSize: 14,
            labelLineHeight: 18,
            descriptionFontSize: 14,
            descriptionLineHeight: 18,
            errorFontSize: 12,
            errorLineHeight: 14,
            errorGap: 6,
            radius: shape.input.sm.borderRadius,
            borderWidth: 1,
          },
          xs: {
            box: 72,
            padding: 8,
            gap: 4,
            iconSize: 18,
            spinnerSize: "sm" as const,
            labelFontSize: 12,
            labelLineHeight: 16,
            descriptionFontSize: 12,
            descriptionLineHeight: 16,
            errorFontSize: 11,
            errorLineHeight: 13,
            errorGap: 4,
            radius: shape.input.xs.borderRadius,
            borderWidth: 1,
          },
        };
        return configs[size];
      },
      dragDropUploader: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const base = {
          width: 360,
          rootGap: 12,
          radius: 4,
          borderWidth: 1,
          textGap: 6,
          titleFontSize: 14,
          titleLineHeight: 18,
          descriptionFontSize: 14,
          descriptionLineHeight: 18,
          errorFontSize: 12,
          errorLineHeight: 16,
          listGap: 6,
          itemGap: 8,
          itemPaddingVertical: 8,
          itemPaddingLeft: 8,
          itemPaddingRight: 12,
          itemIconWrapperSize: 32,
          itemIconSize: 20,
          itemTitleFontSize: 14,
          itemTitleLineHeight: 16,
          itemDescriptionFontSize: 14,
          itemDescriptionLineHeight: 18,
          removeIconSize: 18,
        };
        const configs = {
          xl: {
            ...base,
            areaHeight: 240,
            areaPadding: 20,
            areaGap: 16,
            iconSize: 32,
          },
          lg: {
            ...base,
            areaHeight: 200,
            areaPadding: 20,
            areaGap: 16,
            iconSize: 24,
          },
          md: {
            ...base,
            areaHeight: 160,
            areaPadding: 20,
            areaGap: 16,
            iconSize: 24,
          },
          sm: {
            ...base,
            areaHeight: 128,
            areaPadding: 20,
            areaGap: 16,
            iconSize: 24,
          },
          xs: {
            ...base,
            width: 320,
            rootGap: 8,
            areaHeight: 104,
            areaPadding: 16,
            areaGap: 8,
            textGap: 4,
            iconSize: 16,
            titleFontSize: 12,
            titleLineHeight: 16,
            descriptionFontSize: 12,
            descriptionLineHeight: 16,
            listGap: 4,
            itemGap: 4,
            itemPaddingVertical: 4,
            itemPaddingLeft: 4,
            itemIconWrapperSize: 24,
            itemIconSize: 16,
            itemTitleFontSize: 12,
            itemTitleLineHeight: 14,
            itemDescriptionFontSize: 12,
            itemDescriptionLineHeight: 16,
            removeIconSize: 14,
          },
        };
        return configs[size];
      },
      inputPin: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            size: 64,
            gap: 10,
            fontSize: 20,
            radius: shape.input.xl.borderRadius,
            borderWidth: 1,
            fieldGap: 8,
          },
          lg: {
            size: 56,
            gap: 8,
            fontSize: 18,
            radius: shape.input.lg.borderRadius,
            borderWidth: 1,
            fieldGap: 6,
          },
          md: {
            size: 48,
            gap: 6,
            fontSize: 16,
            radius: shape.input.md.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
          },
          sm: {
            size: 40,
            gap: 4,
            fontSize: 14,
            radius: shape.input.sm.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
          },
          xs: {
            size: 32,
            gap: 4,
            fontSize: 12,
            radius: shape.input.xs.borderRadius,
            borderWidth: 1,
            fieldGap: 4,
          },
        };
        return configs[size];
      },
      textarea: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            height: 144,
            padding: spacing.xl,
            fontSize: 20,
            iconSize: 32,
            radius: shape.input.xl.borderRadius,
          },
          lg: {
            height: 128,
            padding: spacing.l,
            fontSize: 18,
            iconSize: 28,
            radius: shape.input.lg.borderRadius,
          },
          md: {
            height: 112,
            padding: spacing.m,
            fontSize: 16,
            iconSize: 24,
            radius: shape.input.md.borderRadius,
          },
          sm: {
            height: 96,
            padding: spacing.s,
            fontSize: 14,
            iconSize: 20,
            radius: shape.input.sm.borderRadius,
          },
          xs: {
            height: 80,
            padding: spacing.xs,
            fontSize: 12,
            iconSize: 16,
            radius: shape.input.xs.borderRadius,
          },
        };
        return configs[size];
      },
      switch: (size: "xl" | "lg" | "md" | "sm") => {
        const configs = {
          xl: {
            width: 40,
            height: 22,
            knobSize: 18,
            fontSize: 20,
            lineHeight: 22,
            descriptionFontSize: 18,
            descriptionLineHeight: 20,
            labelGap: 12,
            textGap: 4,
            frameBorderRadius: shape.switch.xl.borderRadius,
            knobBorderRadius: shape.knob.xl.borderRadius,
          },
          lg: {
            width: 36,
            height: 20,
            knobSize: 16,
            fontSize: 18,
            lineHeight: 20,
            descriptionFontSize: 16,
            descriptionLineHeight: 18,
            labelGap: 10,
            textGap: 2,
            frameBorderRadius: shape.switch.lg.borderRadius,
            knobBorderRadius: shape.knob.lg.borderRadius,
          },
          md: {
            width: 32,
            height: 18,
            knobSize: 14,
            fontSize: 16,
            lineHeight: 18,
            descriptionFontSize: 14,
            descriptionLineHeight: 16,
            labelGap: 8,
            textGap: 2,
            frameBorderRadius: shape.switch.md.borderRadius,
            knobBorderRadius: shape.knob.md.borderRadius,
          },
          sm: {
            width: 28,
            height: 16,
            knobSize: 12,
            fontSize: 14,
            lineHeight: 16,
            descriptionFontSize: 12,
            descriptionLineHeight: 14,
            labelGap: 8,
            textGap: 0,
            frameBorderRadius: shape.switch.sm.borderRadius,
            knobBorderRadius: shape.knob.sm.borderRadius,
          },
        };
        return configs[size];
      },
      avatar: (size: "xl" | "lg" | "md" | "sm" | "xs" | "xxs") => {
        // Badge offsets adjusted for XL badge size (20px)
        const configs = {
          xl: {
            size: 64,
            fontSize: 20,
            iconSize: 32,
            badgeSize: 20,
            badgeOffsetCircle: { right: -4, top: -4 },
            badgeOffsetSquare: { right: -8, top: -8 },
            borderRadiusSquare: shape.avatar.xl.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
          lg: {
            size: 56,
            fontSize: 20,
            iconSize: 28,
            badgeSize: 20,
            badgeOffsetCircle: { right: -4, top: -4 },
            badgeOffsetSquare: { right: -8, top: -8 },
            borderRadiusSquare: shape.avatar.lg.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
          md: {
            size: 48,
            fontSize: 18,
            iconSize: 24,
            badgeSize: 20,
            badgeOffsetCircle: { right: -5, top: -5 },
            badgeOffsetSquare: { right: -8, top: -8 },
            borderRadiusSquare: shape.avatar.md.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
          sm: {
            size: 40,
            fontSize: 16,
            iconSize: 20,
            badgeSize: 20,
            badgeOffsetCircle: { right: -6, top: -6 },
            badgeOffsetSquare: { right: -8, top: -8 },
            borderRadiusSquare: shape.avatar.sm.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
          xs: {
            size: 32,
            fontSize: 12,
            iconSize: 16,
            badgeSize: 8,
            badgeOffsetCircle: { right: -1, top: -1 },
            badgeOffsetSquare: { right: -3, top: -3 },
            borderRadiusSquare: shape.avatar.xs.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
          xxs: {
            size: 24,
            fontSize: 10,
            iconSize: 12,
            badgeSize: 6,
            badgeOffsetCircle: { right: -1, top: -1 },
            badgeOffsetSquare: { right: -2, top: -2 },
            borderRadiusSquare: shape.avatar.xxs.borderRadius,
            borderRadiusCircle: radiusScale["999"],
          },
        };
        return configs[size];
      },
      tag: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            height: 48,
            padding: 13,
            fontSize: 20,
            lineHeight: 22,
            gap: 6,
            iconSize: 22,
            radius: shape.tag.xl.borderRadius,
            borderWidth: 1,
          },
          lg: {
            height: 40,
            padding: 10,
            fontSize: 18,
            lineHeight: 20,
            gap: 6,
            iconSize: 20,
            radius: shape.tag.lg.borderRadius,
            borderWidth: 1,
          },
          md: {
            height: 32,
            padding: 7,
            fontSize: 16,
            lineHeight: 18,
            gap: 4,
            iconSize: 18,
            radius: shape.tag.md.borderRadius,
            borderWidth: 1,
          },
          sm: {
            height: 24,
            padding: 4,
            fontSize: 14,
            lineHeight: 16,
            gap: 4,
            iconSize: 16,
            radius: shape.tag.sm.borderRadius,
            borderWidth: 1,
          },
          xs: {
            height: 20,
            padding: 3,
            fontSize: 12,
            lineHeight: 14,
            gap: 4,
            iconSize: 14,
            radius: shape.tag.xs.borderRadius,
            borderWidth: 1,
          },
        };
        return configs[size];
      },
      divider: (size: "lg" | "md" | "sm") => {
        const configs = {
          lg: { height: 18, fontSize: 16, lineWeight: 1 },
          md: { height: 16, fontSize: 14, lineWeight: 1 },
          sm: { height: 14, fontSize: 12, lineWeight: 1 },
        };
        return configs[size];
      },
      spinner: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: { size: 96, strokeWidth: 10 },
          lg: { size: 48, strokeWidth: 5 },
          md: { size: 24, strokeWidth: 2 },
          sm: { size: 16, strokeWidth: 2 },
          xs: { size: 8, strokeWidth: 1 },
        };
        return configs[size];
      },
      tabs: (size: "xl" | "lg" | "md" | "sm") => {
        const configs = {
          xl: {
            height: 64,
            fontSize: 20,
            lineHeight: 22,
            iconSize: 24,
            gap: 10,
            paddingHorizontal: 20,
          },
          lg: {
            height: 56,
            fontSize: 18,
            lineHeight: 20,
            iconSize: 20,
            gap: 8,
            paddingHorizontal: 20,
          },
          md: {
            height: 48,
            fontSize: 16,
            lineHeight: 18,
            iconSize: 18,
            gap: 6,
            paddingHorizontal: 20,
          },
          sm: {
            height: 40,
            fontSize: 14,
            lineHeight: 16,
            iconSize: 16,
            gap: 4,
            paddingHorizontal: 20,
          },
        };
        return configs[size];
      },
      segmented: (size: "xl" | "lg" | "md" | "sm") => {
        // Sizing based on Figma: node-id=5867-44879
        const configs = {
          xl: {
            height: 64,
            containerPadding: 4,
            containerRadius: shape.segmented.xl.borderRadius,
            itemHeight: 56,
            itemPaddingHorizontal: 20,
            itemPaddingVertical: 10,
            itemRadius: shape.segmentedItem.xl.borderRadius,
            fontSize: 20,
            lineHeight: 22,
            iconSize: 24,
            gap: 8,
          },
          lg: {
            height: 56,
            containerPadding: 4,
            containerRadius: shape.segmented.lg.borderRadius,
            itemHeight: 48,
            itemPaddingHorizontal: 20,
            itemPaddingVertical: 10,
            itemRadius: shape.segmentedItem.lg.borderRadius,
            fontSize: 18,
            lineHeight: 20,
            iconSize: 24,
            gap: 8,
          },
          md: {
            height: 48,
            containerPadding: 4,
            containerRadius: shape.segmented.md.borderRadius,
            itemHeight: 40,
            itemPaddingHorizontal: 16,
            itemPaddingVertical: 8,
            itemRadius: shape.segmentedItem.md.borderRadius,
            fontSize: 16,
            lineHeight: 18,
            iconSize: 24,
            gap: 6,
          },
          sm: {
            height: 40,
            containerPadding: 4,
            containerRadius: shape.segmented.sm.borderRadius,
            itemHeight: 32,
            itemPaddingHorizontal: 12,
            itemPaddingVertical: 6,
            itemRadius: shape.segmentedItem.sm.borderRadius,
            fontSize: 14,
            lineHeight: 16,
            iconSize: 20,
            gap: 4,
          },
        };
        return configs[size];
      },
      tabsSegmented: (size: "xl" | "lg" | "md" | "sm") => {
        // Sizing based on Figma: node-id=5986-3922
        const configs = {
          xl: {
            height: 64,
            containerPadding: 4,
            itemPaddingHorizontal: 16,
            itemPaddingVertical: 18,
            fontSize: 20,
            lineHeight: 22,
            iconSize: 24,
            gap: 8,
          },
          lg: {
            height: 56,
            containerPadding: 4,
            itemPaddingHorizontal: 16,
            itemPaddingVertical: 14,
            fontSize: 18,
            lineHeight: 20,
            iconSize: 24,
            gap: 8,
          },
          md: {
            height: 48,
            containerPadding: 4,
            itemPaddingHorizontal: 12,
            itemPaddingVertical: 11,
            fontSize: 16,
            lineHeight: 18,
            iconSize: 24,
            gap: 6,
          },
          sm: {
            height: 40,
            containerPadding: 3,
            itemPaddingHorizontal: 12,
            itemPaddingVertical: 8,
            fontSize: 14,
            lineHeight: 16,
            iconSize: 20,
            gap: 6,
          },
        };
        return configs[size];
      },
      checkboxTagGroup: (size: "xl" | "lg" | "md" | "sm") => {
        const configs = {
          xl: {
            height: 64,
            paddingHorizontal: 16,
            paddingVertical: 21,
            fontSize: 20,
            lineHeight: 22,
            gap: 8,
            borderRadius: radiusScale["50"],
          },
          lg: {
            height: 56,
            paddingHorizontal: 16,
            paddingVertical: 18,
            fontSize: 18,
            lineHeight: 20,
            gap: 8,
            borderRadius: radiusScale["50"],
          },
          md: {
            height: 48,
            paddingHorizontal: 12,
            paddingVertical: 15,
            fontSize: 16,
            lineHeight: 18,
            gap: 8,
            borderRadius: radiusScale["25"],
          },
          sm: {
            height: 40,
            paddingHorizontal: 12,
            paddingVertical: 11,
            fontSize: 14,
            lineHeight: 16,
            gap: 4,
            borderRadius: radiusScale["25"],
          },
        };
        return configs[size];
      },
      badge: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: {
            size: 20,
            fontSize: 12,
            lineHeight: 14,
            iconSize: 12,
            padding: 6,
          },
          lg: {
            size: 16,
            fontSize: 10,
            lineHeight: 10,
            iconSize: 10,
            padding: 4,
          },
          md: {
            size: 12,
            fontSize: 10,
            lineHeight: 10,
            iconSize: 8,
            padding: 3,
          },
          sm: { size: 8, fontSize: 8, lineHeight: 8, iconSize: 6, padding: 0 },
          xs: { size: 4, fontSize: 4, lineHeight: 4, iconSize: 4, padding: 0 },
        };
        return configs[size];
      },
      notification: (type: "toast" | "inline") => {
        const configs = {
          toast: {
            width: 400,
            paddingHorizontal: 20,
            paddingVertical: 16,
            gap: 16,
            titleSize: 18,
            messageSize: 14,
            iconSize: 20,
            iconWrapperSize: 32,
            radius: shape.toast.borderRadius,
          },
          inline: {
            width: "100%",
            paddingHorizontal: 20,
            paddingVertical: 16,
            gap: 28,
            titleSize: 18,
            messageSize: 14,
            iconSize: 20,
            iconWrapperSize: 32,
            radius: shape.modal.borderRadius,
          },
        };
        return configs[type];
      },
      notificationPanel: () => ({
        minHeight: 66,
        iconFrameWidth: 54,
        iconSize: 24,
        borderRadius: radiusScale["100"],
        bodyPaddingHorizontal: 16,
        bodyPaddingVertical: 12,
        textGap: 2,
        contentGap: 24,
        buttonsGap: 8,
        closeButtonSize: 32,
        titleFontSize: 16,
        titleLineHeight: 20,
        descriptionFontSize: 14,
        descriptionLineHeight: 20,
      }),
      progress: (
        size: "xl" | "lg" | "md" | "sm" | "xs" | "xxs" | "l" | "m" | "s"
      ) => {
        const configs = {
          xl: { height: 10, labelSize: 16, helperSize: 14, gap: 10 },
          lg: { height: 8, labelSize: 14, helperSize: 12, gap: 8 },
          md: { height: 6, labelSize: 12, helperSize: 12, gap: 6 },
          sm: { height: 4, labelSize: 12, helperSize: 12, gap: 4 },
          xs: { height: 2, labelSize: 10, helperSize: 10, gap: 2 },
          xxs: { height: 2, labelSize: 10, helperSize: 10, gap: 2 },
          l: { height: 8, labelSize: 16, helperSize: 14, gap: 10 },
          m: { height: 6, labelSize: 14, helperSize: 12, gap: 8 },
          s: { height: 4, labelSize: 12, helperSize: 12, gap: 6 },
        };
        return configs[size];
      },
      supportingText: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        const configs = {
          xl: { fontSize: 18, lineHeight: "20px", gap: 6, iconSize: 18 },
          lg: { fontSize: 16, lineHeight: "18px", gap: 6, iconSize: 18 },
          md: { fontSize: 14, lineHeight: "16px", gap: 6, iconSize: 18 },
          sm: { fontSize: 12, lineHeight: "14px", gap: 4, iconSize: 16 },
          xs: { fontSize: 10, lineHeight: "10px", gap: 4, iconSize: 16 },
        };
        return configs[size];
      },
      iconWrapper: (size: "xl" | "lg" | "md" | "sm" | "xs" | "xxs") => {
        const configs = {
          xl: { size: 56, iconSize: 32, fontSize: 24 },
          lg: { size: 48, iconSize: 28, fontSize: 20 },
          md: { size: 40, iconSize: 24, fontSize: 18 },
          sm: { size: 32, iconSize: 20, fontSize: 16 },
          xs: { size: 24, iconSize: 16, fontSize: 14 },
          xxs: { size: 18, iconSize: 12, fontSize: 12 },
        };
        return configs[size];
      },
      stepper: (size: "md" | "sm") => {
        const configs = {
          md: {
            iconSize: 32,
            titleSize: 16,
            descSize: 14,
            gap: 12,
            currentTitleSize: 20,
            currentDescSize: 14,
            currentPadding: 24,
            tailSize: 5,
          },
          sm: {
            iconSize: 24,
            titleSize: 14,
            descSize: 12,
            gap: 8,
            currentTitleSize: 16,
            currentDescSize: 12,
            currentPadding: 20,
            tailSize: 3,
          },
        };
        return configs[size];
      },
      contextMenu: (size: "xl" | "lg" | "md" | "sm") => {
        // Sizing values from Figma design: node-id=5867-47111
        const configs = {
          xl: {
            paddingVertical: 0,
            itemPaddingHorizontal: 16,
            itemPaddingVertical: 18,
            fontSize: 20,
            lineHeight: 26,
            descriptionFontSize: 18,
            iconSize: 18,
            gap: 12,
            minWidth: 276,
            panelWidth: 320,
            statusDotSize: 12,
            iconWrapperSize: 40,
            triggerOffset: 4,
            borderRadius: 8,
            // Search sizing from Figma
            searchPaddingHorizontal: 16,
            searchPaddingVertical: 18,
            searchGap: 18,
            searchFontSize: 20,
            searchLineHeight: 26,
            searchIconSize: 18,
          },
          lg: {
            paddingVertical: 0,
            itemPaddingHorizontal: 14,
            itemPaddingVertical: 14,
            fontSize: 18,
            lineHeight: 24,
            descriptionFontSize: 16,
            iconSize: 18,
            gap: 10,
            minWidth: 220,
            panelWidth: 296,
            statusDotSize: 10,
            iconWrapperSize: 36,
            triggerOffset: 4,
            borderRadius: 8,
            // Search sizing from Figma
            searchPaddingHorizontal: 14,
            searchPaddingVertical: 14,
            searchGap: 14,
            searchFontSize: 18,
            searchLineHeight: 24,
            searchIconSize: 18,
          },
          md: {
            paddingVertical: 0,
            itemPaddingHorizontal: 12,
            itemPaddingVertical: 11,
            fontSize: 16,
            lineHeight: 20,
            descriptionFontSize: 14,
            iconSize: 18,
            gap: 8,
            minWidth: 172,
            panelWidth: 272,
            statusDotSize: 8,
            iconWrapperSize: 32,
            triggerOffset: 4,
            borderRadius: 8,
            // Search sizing from Figma
            searchPaddingHorizontal: 12,
            searchPaddingVertical: 11,
            searchGap: 10,
            searchFontSize: 16,
            searchLineHeight: 20,
            searchIconSize: 18,
          },
          sm: {
            paddingVertical: 0,
            itemPaddingHorizontal: 10,
            itemPaddingVertical: 8,
            fontSize: 14,
            lineHeight: 18,
            descriptionFontSize: 12,
            iconSize: 18,
            gap: 8,
            minWidth: 132,
            panelWidth: 248,
            statusDotSize: 6,
            iconWrapperSize: 24,
            triggerOffset: 4,
            borderRadius: 4,
            // Search sizing from Figma
            searchPaddingHorizontal: 10,
            searchPaddingVertical: 7,
            searchGap: 6,
            searchFontSize: 14,
            searchLineHeight: 18,
            searchIconSize: 18,
          },
        };
        return configs[size];
      },
      modal: () => {
        // Sizing values from Figma design: node-id=6910-5615
        return {
          borderRadius: shape.modal.borderRadius,
          headerPadding: "24px 24px 0",
          contentPadding: 24,
          /** @deprecated Header buttons no longer use IconButton sizes. */
          headerButtonSize: "xs" as const,
          headerIconSize: 28,
          headerButtonPadding: 4,
          headerButtonRadius: 4,
          headerMinHeight: 44,
          headerGap: 16,
          footerGap: 16,
          /** @deprecated Modal elevation comes from the scrim, not a drop shadow. */
          shadow:
            "0px 8px 12px 6px rgba(7, 7, 8, 0.1), 0px 4px 4px rgba(7, 7, 8, 0.2)",
          maxWidth: 680,
          overlayColor: "rgba(0, 0, 0, 0.75)",
          portalPadding: 20,
          zIndex: 1000,
        };
      },
      drawer: () => ({
        // Sizing values from Figma design: B2B Components Library node-id=8291-103845
        widthSm: 480,
        widthMd: 620,
        widthLg: 1056,
        headerHeight: 80,
        headerPaddingX: 32,
        headerPaddingY: 24,
        headerGap: 24,
        titleFontSize: 18,
        titleLineHeight: 24,
        contentPaddingX: 32,
        footerPadding: 32,
        outerPadding: 8,
        containerRadius: 4,
        contentRadius: 8,
        stepperPadding: 16,
        stepperRadius: 8,
        stepperContentGap: 8,
        scrimColor: "rgba(0, 0, 0, 0.75)",
        animationDuration: 300,
        zIndex: 1000,
      }),
      sidebar: () => ({
        // Sizing values from Figma design: B2B Components Library
        widthExpanded: 280,
        widthCollapsed: 48,
        radius: 4,
        itemHeight: 32,
        iconSize: 18,
        padding: 8,
        sectionLabelHeight: 20,
        // Aligns nested item text with parent label (padding 8 + icon 18 + gap 8 = 34)
        // and centers the rail under the parent icon (padding 8 + iconSize/2 = 17).
        nestedItemPaddingLeft: 34,
        nestedItemRailLeft: 17,
        chevronSize: 14,
        externalIconSize: 18,
        chatBadgeSize: 6,
        itemBadgeSize: 4,
        focusOutlineWidth: 2,
        scrollbarWidth: 4,
        popoverGap: 12,
        popoverViewportPadding: 8,
        popoverMinWidth: 180,
        popoverMaxWidth: 240,
        popoverShadow: "0 2px 25px rgba(7, 7, 8, 0.15)",
        popoverZIndex: 1000,
        transitionWidth: "0.25s ease-in-out",
        transitionFadeIn: "opacity 0.15s ease-in-out 0.1s",
        transitionFadeOut: "opacity 0.1s ease-in-out",
        transitionItem:
          "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
        transitionRows: "grid-template-rows 0.25s ease-in-out",
      }),
      stepperB2b: () => ({
        // Sizing values from Figma design: B2B Components Library
        // variant grid node-id=10999-17988, state grid node-id=10259-5501
        chipSize: 32,
        chipRadius: 2,
        chipPaddingTop: 5,
        chipPaddingBottom: 4,
        chipPaddingX: 4,
        chipIconSize: 16,
        numberFontSize: 18,
        numberLineHeight: 24,
        titleFontSize: 16,
        titleLineHeight: 20,
        descriptionFontSize: 14,
        descriptionLineHeight: 20,
        captionFontSize: 12,
        captionLineHeight: 16,
        itemGap: 12,
        contentGap: 4,
        contentMinHeight: 32,
        contentWidth: 128,
        activeCardRadiusVertical: 4,
        activeCardRadiusHorizontal: 6,
        activeCardPaddingTop: 24,
        activeCardPaddingX: 24,
        activeCardPaddingYHorizontal: 12,
        activeCardPaddingLeftHorizontal: 12,
        activeCardGapHorizontal: 8,
        innerConnectorLength: 48,
        connectorSegment: 24,
        connectorCellHeight: 24,
        connectorCellWidth: 80,
        surfaceRadius: 8,
        surfacePaddingVertical: 16,
        surfacePaddingHorizontal: 8,
        horizontalItemGap: 16,
        horizontalFirstItemWidth: 220,
        horizontalItemWidth: 236,
        horizontalConnectorSegment: 32,
        horizontalConnectorGap: 12,
        simpleCardPadding: 12,
        simpleCardRadius: 4,
        simpleCardGap: 4,
        simpleItemGap: 4,
        simpleHorizontalCellWidth: 56,
        simpleHorizontalStubLength: 8,
      }),
      collapsibleB2b: () => ({
        // Sizing values from Figma design: B2B Components Library node-id=10469-6509
        triggerPadding: 16,
        triggerGap: 12,
        triggerMinHeight: 56,
        surfaceRadius: radiusScale["50"],
        cellRadius: radiusScale["100"],
        iconSlotSize: 32,
        iconSlotRadius: radiusScale["50"],
        chevronSize: 24,
        trailingGap: 8,
        textGap: 2,
        titleFontSize: 16,
        titleLineHeight: 20,
        captionFontSize: 12,
        captionLineHeight: 18,
        panelPadding: 16,
        accordionItemGap: 8,
      }),
      toast: () => {
        // Sizing values from Figma design: node-id=3044-8872
        return {
          minHeight: 64,
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: shape.toast.borderRadius,
          gap: 12,
          iconSize: 24,
          closeButtonSize: 24,
          closeIconSize: 20,
          fontSize: 16,
          lineHeight: 20,
          maxWidth: 400,
          containerPadding: 12,
          groupGap: 4,
        };
      },
      toggleButtonGroup: (size: "xl" | "lg" | "md" | "sm" | "xs") => {
        // Sizing values from Figma design: node-id=6409-34323
        const configs = {
          xl: {
            height: 64,
            paddingHorizontal: 21,
            fontSize: 20,
            lineHeight: 22,
            iconSize: 22,
            gap: 6,
            borderRadius: shape.toggleButtonGroup.xl.borderRadius,
            itemGap: 8,
          },
          lg: {
            height: 56,
            paddingHorizontal: 18,
            fontSize: 18,
            lineHeight: 20,
            iconSize: 20,
            gap: 4,
            borderRadius: shape.toggleButtonGroup.lg.borderRadius,
            itemGap: 8,
          },
          md: {
            height: 48,
            paddingHorizontal: 15,
            fontSize: 16,
            lineHeight: 18,
            iconSize: 18,
            gap: 4,
            borderRadius: shape.toggleButtonGroup.md.borderRadius,
            itemGap: 6,
          },
          sm: {
            height: 40,
            paddingHorizontal: 12,
            fontSize: 14,
            lineHeight: 16,
            iconSize: 16,
            gap: 4,
            borderRadius: shape.toggleButtonGroup.sm.borderRadius,
            itemGap: 4,
          },
          xs: {
            height: 32,
            paddingHorizontal: 9,
            fontSize: 12,
            lineHeight: 14,
            iconSize: 14,
            gap: 4,
            borderRadius: shape.toggleButtonGroup.xs.borderRadius,
            itemGap: 4,
          },
        };
        return configs[size];
      },
      table: {
        containerRadius: 8,
        containerPaddingVertical: 0,
        containerGap: 0,
        captionFontSize: 14,
        captionLineHeight: 20,
        headerRowHeight: 56,
        headerRowPaddingHorizontal: 24,
        headerCellGap: 4,
        headerCellFontSize: 14,
        headerCellLineHeight: 18,
        rowHeight: 56,
        rowPaddingHorizontal: 24,
        cellGap: 16,
        cellFontSize: 14,
        cellLineHeight: 18,
        paginationGap: 16,
        paginationPaddingHorizontal: 24,
      },
    },
  };
};

/**
 * The fully-resolved theme object returned by {@link themeConfig} and exposed to
 * components via `useDesignSystem().theme` and `useResolvedTheme().theme`.
 *
 * This is the single canonical theme type. Import it when building custom
 * components that need to receive, store, or pass the theme around:
 *
 * ```tsx
 * import { useResolvedTheme, type Theme } from "@xsolla/xui-core";
 *
 * const surface = (theme: Theme) => theme.colors.background.secondary;
 * ```
 */
export type Theme = ReturnType<typeof themeConfig>;

/** Resolved color tokens for the active theme mode (`theme.colors`). */
export type ThemeColorScheme = Theme["colors"];

/** Spacing scale — `xs`…`xl` (`theme.spacing`). */
export type ThemeSpacing = Theme["spacing"];

/** Semantic shape tokens: border-radius + border-width (`theme.shape`). */
export type ThemeShape = Theme["shape"];

/** Elevation / shadow tokens (`theme.shadow`). */
export type ThemeShadow = Theme["shadow"];

/** Resolved font families for the active product context (`theme.fonts`). */
export type ThemeFonts = Theme["fonts"];

/** Typography role families — `primary` / `heading` / `body` (`theme.typography`). */
export type ThemeTypographyRoles = Theme["typography"];

/** Per-component sizing-config accessors (`theme.sizing`). */
export type ThemeSizing = Theme["sizing"];

export interface DesignSystemContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  productContext: ProductContext;
  setProductContext: (productContext: ProductContext) => void;
  theme: Theme;
  /** Whether the app uses responsive typography (CSS vars + @media). Defaults to true. */
  responsive: boolean;
}

const DesignSystemContext = createContext<DesignSystemContextType | undefined>(
  undefined
);

export const XUIProvider: React.FC<{
  children: React.ReactNode;
  initialMode?: ThemeMode;
  initialProductContext?: ProductContext;
  /** Use `initialProductContext` instead. */
  productContext?: never;
  /** Load toolkit fonts from the Xsolla CDN. Defaults to true on web, no-ops on native. */
  loadFonts?: boolean;
  /**
   * Enable responsive typography via CSS custom properties and @media queries.
   * When false, all components use fixed (common/desktop) values — suitable for
   * apps that don't need mobile scaling. Defaults to true.
   */
  responsive?: boolean;
}> = ({
  children,
  initialMode = "dark",
  initialProductContext = defaultProductContext,
  loadFonts = true,
  responsive = true,
}) => {
  const [mode, setMode] = useState<ThemeMode>(initialMode);
  const [productContext, setProductContext] = useState<ProductContext>(
    initialProductContext
  );

  const value = useMemo(
    () => ({
      mode,
      setMode,
      productContext,
      setProductContext,
      theme: themeConfig(mode, productContext),
      responsive,
    }),
    [mode, productContext, responsive]
  );

  return (
    <DesignSystemContext.Provider value={value}>
      {loadFonts && <FontLoader />}
      {responsive && <TypographyStyleLoader />}
      {children}
    </DesignSystemContext.Provider>
  );
};

export const useDesignSystem = (): DesignSystemContextType => {
  const context = useContext(DesignSystemContext);
  if (!context) {
    // Return default theme if provider is missing
    return {
      mode: "dark" as ThemeMode,
      setMode: () => {},
      productContext: defaultProductContext,
      setProductContext: () => {},
      theme: themeConfig("dark", defaultProductContext),
      responsive: true,
    };
  }
  return context;
};

export interface ThemeOverrideProps {
  /** Override the global theme mode for this component */
  themeMode?: ThemeMode;
  /** Override the global product context (fonts/typography) for this component */
  themeProductContext?: ProductContext;
}

export const useResolvedTheme = (
  overrides?: ThemeOverrideProps
): DesignSystemContextType => {
  const context = useDesignSystem();

  const resolvedMode = overrides?.themeMode ?? context.mode;
  const resolvedProductContext =
    overrides?.themeProductContext ?? context.productContext;

  const theme = useMemo(() => {
    if (
      resolvedMode === context.mode &&
      resolvedProductContext === context.productContext
    ) {
      return context.theme;
    }
    return themeConfig(resolvedMode, resolvedProductContext);
  }, [
    resolvedMode,
    resolvedProductContext,
    context.mode,
    context.productContext,
    context.theme,
  ]);

  return {
    ...context,
    mode: resolvedMode,
    productContext: resolvedProductContext,
    theme,
  };
};

let idCounter = 0;

const hasReactUseId = typeof React.useId === "function";

const useIdFallback = (): string => {
  const [id] = useState(() => `xui-${++idCounter}`);
  return id;
};

export const useId: () => string = hasReactUseId ? React.useId : useIdFallback;

/**
 * Context for the current modal's ID.
 * Used by portal-based components (tooltips, dropdowns) to mark their content
 * as belonging to a modal, preventing click-outside from closing the modal.
 */
export const ModalIdContext = createContext<string | null>(null);

/**
 * Returns the current modal's ID for use with portal content.
 * Apply as `data-modal-id={modalId}` to portal containers.
 * Returns null if not inside a modal.
 */
export const useModalId = (): string | null => useContext(ModalIdContext);

export * from "./tokens/colors";
export * from "./tokens/spacing";
export * from "./tokens/shape";
export * from "./tokens/shadows";
export * from "./tokens/fonts";
export * from "./tokens/typography";
export * from "./tokens/breakpoints";
export * from "./tokens/responsive-typography";
export * from "./platform";
export { FontLoader } from "./fonts/FontLoader";
export { fontFacesCSS } from "./fonts/fontFaces";
export { TypographyStyleLoader } from "./styles/TypographyStyleLoader";
export { generateTypographyCSS, cssVar } from "./styles/typography-css";
export type { ProductContext };
